import { API } from '@/api/API'
import { logInTrackJS } from '@/errors/helpers'
import { auth0 } from '@/helpers/Auth0Client'
import { externalUrlAuth } from '@/helpers/ExternalUrlAuth'
import { sfec } from '@/helpers/SFECClient'
import store from '@/store/store'
import { getCookie } from 'tiny-cookie'
import type VueRouter from 'vue-router'
import type { Route } from 'vue-router'

export type LoginAttemptResult = {
  success: boolean
  error?: Error
}

type ExternalAuthMethod = 'auth0' | 'sfec' | 'external_url'

function getAuthMethod(): ExternalAuthMethod | undefined {
  if (auth0.isAuth0()) return 'auth0'
  if (sfec.isSFEC()) return 'sfec'
  if (externalUrlAuth.isExternalUrlAuth()) return 'external_url'
}

function isExternalAuth() {
  return getAuthMethod() !== undefined
}

async function login(currentPath: string): Promise<LoginAttemptResult> {
  let result: LoginAttemptResult | Promise<LoginAttemptResult>

  switch (getAuthMethod()) {
    case 'auth0':
      result = auth0.login(currentPath)
      break
    case 'sfec':
      result = sfec.login(currentPath)
      break
    case 'external_url':
      result = externalUrlAuth.login()
      break
    default:
      result = { success: false, error: new Error('Unknown auth method') }
  }

  return await result
}

async function logout(nextPath: string): Promise<LoginAttemptResult> {
  switch (getAuthMethod()) {
    case 'auth0': {
      await API.post('logout')
      await store.dispatch('clearAll')
      const returnTo = window.location.origin + nextPath
      return auth0.logout(returnTo)
    }
    case 'sfec': {
      await API.post('logout')
      await store.dispatch('clearAll')
      window.location.assign(nextPath)
      return { success: true }
    }
    case 'external_url':
      await store.dispatch('clearAll')
      return externalUrlAuth.logout()
    default:
      return { success: false, error: new Error('Unknown auth method') }
  }
}

async function handleRedirectCallback(router?: VueRouter, to?: Route): Promise<LoginAttemptResult> {
  let result: LoginAttemptResult
  let isGlobalError = false

  switch (getAuthMethod()) {
    case 'auth0':
      result = await auth0.handleRedirectCallback()
      break

    case 'sfec': {
      if (router && to) {
        result = sfec.handleRedirectCallback(router, to)
        isGlobalError = Boolean(result.error)
      } else {
        result = { success: false, error: new Error('Missing handler arguments') }
      }

      break
    }

    default:
      result = { success: false, error: new Error('Unknown auth method') }
  }

  if (result.error && isGlobalError) showGlobalError(result.error)

  return result
}

/* An unhandled promise rejection is caught by the global error handler without interrupting the regular code flow */
function showGlobalError(error: Error) {
  Promise.reject(error)
  logInTrackJS(`AuthExternal error: ${error.message}`, error)
}

/** Checks if the user has a session cookie but no identity, which means they have been logged in from outside the app. (e.g. MoS Boston, oauth web server flows). See https://sandbox.cms.d1tix.com/docs/guides/authenticate_as_member/ */
function isLoggedInExternally(): boolean {
  const hasSessionCookie = getCookie('session_expires')
  const identity = store.getters['Member/identity']

  return Boolean(hasSessionCookie && !identity)
}

export const authExternal = {
  isExternalAuth,
  login,
  logout,
  handleRedirectCallback,
  isLoggedInExternally,
}

if (window.Cypress) {
  window.__spyOn?.(authExternal)
}

export type AuthExternal = typeof authExternal

declare global {
  interface Window {
    __spiedAuthExternal?: AuthExternal
  }
}
